import { useEffect, useMemo, useState } from 'react';
import {
  DEAL_SOURCES,
  contactSchema,
  dealDetailSchema,
  type Contact,
  type DealDetail,
  type DealSource,
} from '@forecastify/shared';
import { Button, Dialog, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { Field } from '../card';
import { DateField } from '../date-field';
import { parseList } from '../permissions-hook';
import { ContactDialog } from './contact-dialog';
import { dealRefusal, sourceLabel, type DealFeeds } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D3 · *Új deal* — `phase6/05-deal-new-dialog.png`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Name, client, owner and an open status are required; everything else can wait — *"egy beérkező
 * megkeresés is érvényes deal"*. The contact list is the chosen client's own (DM-2), and a
 * missing one can be added from the picker without leaving the dialog.
 */
export function NewDealDialog({
  open,
  feeds,
  defaultOwnerId,
  defaultClientId,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly feeds: DealFeeds;
  readonly defaultOwnerId: string | null;
  readonly defaultClientId?: string | undefined;
  readonly onClose: () => void;
  readonly onCreated: (deal: DealDetail) => void;
}) {
  const t = useT();
  const session = useSession();
  const openStatuses = useMemo(
    () => (feeds.config?.statuses ?? []).filter((status) => status.category === 'open'),
    [feeds.config],
  );

  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [contactId, setContactId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [value, setValue] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [source, setSource] = useState<DealSource>('inbound');
  const [contacts, setContacts] = useState<readonly Contact[]>([]);
  const [addingContact, setAddingContact] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* A fresh form every time it opens. */
  useEffect(() => {
    if (!open) return;
    setName('');
    setClientId(defaultClientId ?? '');
    setContactId('');
    setOwnerId(defaultOwnerId ?? '');
    setStatusId(openStatuses[0]?.id ?? '');
    setValue('');
    setCloseDate('');
    setSource('inbound');
    setError(null);
  }, [open, defaultOwnerId, defaultClientId, openStatuses]);

  useEffect(() => {
    if (clientId === '') {
      setContacts([]);
      return;
    }
    void (async () => {
      const response = await session
        .request(`/contacts?client=${encodeURIComponent(clientId)}`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(contactSchema, await response.json());
      if (parsed === null) return;
      setContacts(parsed);
      setContactId(parsed.find((contact) => contact.isPrimary)?.id ?? '');
    })();
  }, [clientId, session]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '' || clientId === '' || ownerId === '' || statusId === '') {
      setError(t('error.validation_failed'));
      return;
    }
    const amount = value.trim() === '' ? 0 : Number(value.replace(/\s/gu, '').replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0) {
      setError(t('deals.error.value'));
      return;
    }
    setBusy(true);
    const response = await session
      .request('/deals', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          clientId,
          contactId: contactId === '' ? null : contactId,
          ownerMemberId: ownerId,
          statusId,
          indicativeValue: amount,
          closeDate: closeDate === '' ? null : closeDate,
          source,
        }),
      })
      .catch(() => null);
    setBusy(false);
    if (response === null) {
      setError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      setError(await dealRefusal(response, t));
      return;
    }
    const parsed = dealDetailSchema.safeParse(await response.json());
    if (parsed.success) onCreated(parsed.data);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={t('deals.new.title')}
        description={t('deals.new.hint')}
        closeLabel={t('common.close')}
        width={512}
      >
        <div className="flex flex-col gap-3">
          <Field label={t('deals.new.name')} required>
            <TextInput
              autoFocus
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setError(null);
              }}
              placeholder={t('deals.new.namePlaceholder')}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('deals.new.client')} required>
              <Select
                searchable
                value={clientId}
                onChange={(event) => {
                  setClientId(event.target.value);
                  setError(null);
                }}
                options={[
                  { value: '', label: t('deals.new.pickClient') },
                  ...feeds.clients.map((client) => ({ value: client.id, label: client.name })),
                ]}
              />
            </Field>
            <Field label={t('deals.new.contact')}>
              <Select
                value={contactId}
                disabled={clientId === ''}
                onChange={(event) => {
                  setContactId(event.target.value);
                }}
                options={[
                  { value: '', label: t('deals.new.none') },
                  ...contacts.map((contact) => ({ value: contact.id, label: contact.name })),
                ]}
                {...(clientId === ''
                  ? {}
                  : {
                      action: {
                        label: t('contacts.action.create'),
                        onSelect: () => {
                          setAddingContact(true);
                        },
                      },
                    })}
              />
            </Field>
            <Field label={t('deals.new.owner')} required>
              <Select
                searchable
                value={ownerId}
                onChange={(event) => {
                  setOwnerId(event.target.value);
                }}
                options={feeds.members.map((member) => ({
                  value: member.id,
                  label: member.displayName,
                }))}
              />
            </Field>
            <Field label={t('deals.new.status')} required>
              <Select
                value={statusId}
                onChange={(event) => {
                  setStatusId(event.target.value);
                }}
                options={openStatuses.map((status) => ({ value: status.id, label: status.name }))}
              />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('deals.new.value')}>
              <TextInput
                inputMode="decimal"
                className="tabular-nums"
                value={value}
                placeholder="0"
                onChange={(event) => {
                  setValue(event.target.value);
                  setError(null);
                }}
              />
            </Field>
            <Field label={t('deals.new.close')}>
              <DateField
                value={closeDate === '' ? null : closeDate}
                onChange={setCloseDate}
                label={t('deals.new.close')}
              />
            </Field>
            <Field label={t('deals.new.source')}>
              <Select
                value={source}
                onChange={(event) => {
                  setSource(event.target.value as DealSource);
                }}
                options={DEAL_SOURCES.map((entry) => ({
                  value: entry,
                  label: sourceLabel(t, entry),
                }))}
              />
            </Field>
          </div>

          {error === null ? null : (
            <div className="text-destructive text-[12px]" role="alert">
              {error}
            </div>
          )}

          <div className="mt-1 flex items-center justify-end gap-2">
            <Button variant="outline" size="lg" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button size="lg" disabled={busy} onClick={() => void submit()}>
              {t('deals.new.submit')}
            </Button>
          </div>
        </div>
      </Dialog>
      <ContactDialog
        open={addingContact}
        clientId={clientId}
        contact={null}
        onClose={() => {
          setAddingContact(false);
        }}
        onSaved={(contact) => {
          setContacts((current) => [...current, contact]);
          setContactId(contact.id);
          setAddingContact(false);
        }}
      />
    </>
  );
}
